import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import moment from 'moment'

import Panel from '../ui/Panel'
import VideoModal from '../ui/VideoModal'
import Btn from '../ui/Btn'
import './user.css'

/*
 * COMPLETED CHALLENGES — the evidence.
 *
 * GET manager/dashboard/userchallenges/{uuid}
 *   -> results: [{ FileID, Title, Descr, Points, DateTo }]
 *
 * A scout reads this list for one thing: the video. So the play control is the
 * first column, the points are right-aligned and tabular so a column of them
 * can be compared, and the closing date is labelled as a date rather than left
 * to look like "when this happened".
 *
 * The react-bootstrap Modal became a token-drawn dialog portalled to <body>:
 * it closes on Escape and on the backdrop, and it locks the page behind it.
 * react-video-js-player is unchanged — it is the media layer, not the chrome.
 */

export default function UserActivities(params) {
  const [challenges, setChallenges] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  // -1 = closed. VideoModal owns Escape, the arrows and the scroll lock.
  const [clipIndex, setClipIndex] = useState(-1)

  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    loadData()
    return () => { aliveRef.current = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.uuid])



  const handleClose = () => {
    setClipIndex(-1)
  }

  const handleShow = (row) => {
    const list = challenges.filter((entry) => !!entry.FileID)
    const at = list.indexOf(row)
    if (at !== -1) setClipIndex(at)
  }

  const loadData = () => {
    setIsLoading(true)
    setFailed(false)

    axios.get(process.env.REACT_APP_API_SERVER + 'manager/dashboard/userchallenges/' + params.uuid, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken")
      }
    })
      .then(res => {
        if (!aliveRef.current) return
        setChallenges((res.data && Array.isArray(res.data.results)) ? res.data.results : [])
        setIsLoading(false)
      })
      .catch(err => {
        if (!aliveRef.current) return
        setIsLoading(false)
        setFailed(true)
      })
  }

  const pointsOf = (row) => {
    const n = Number(row.Points)
    return Number.isFinite(n) ? n : 0
  }

  const totalPoints = challenges.reduce((sum, row) => sum + pointsOf(row), 0)

  const dateOf = (row) => {
    if (!row.DateTo) return '—'
    const when = moment(row.DateTo)
    return when.isValid() ? when.format('DD/MM/YYYY') : '—'
  }

  const rows = challenges.map((row, index) => (
    <tr key={(row.FileID || 'row') + '-' + index}>
      <td className="usr-ch-act">
        <Btn
          onClick={() => handleShow(row)}
          disabled={!row.FileID}
          title={row.FileID ? 'Watch the submission' : 'No video on this submission'}
          aria-label={'Watch ' + (row.Title || 'this challenge')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5.5v13l10-6.5-10-6.5z" />
          </svg>
        </Btn>
      </td>
      <td>
        <div className="usr-ch-title">{row.Title || 'Untitled challenge'}</div>
      </td>
      <td className="n">{pointsOf(row)}</td>
      <td className="usr-ch-right">
        <span className="usr-ch-when">{dateOf(row)}</span>
      </td>
    </tr>
  ))

  const clips = challenges.filter((row) => !!row.FileID)

  if (clipIndex >= clips.length && clipIndex !== -1) {
    // Guard rather than crash if a reload trimmed the list.
    setTimeout(() => setClipIndex(-1), 0)
  }

  const dialog = clipIndex >= 0 && clipIndex < clips.length ? (
    <VideoModal
      items={clips.map((row) => ({
        fileId: row.FileID,
        name: params.name || 'This player',
        uuid: params.uuid,
        score: pointsOf(row),
        scoreLabel: 'pts',
        subtitle: row.Title || 'Challenge submission',
      }))}
      index={clipIndex}
      title={params.name || 'Challenge submission'}
      onIndex={setClipIndex}
      onClose={handleClose}
    />
  ) : null

  return (
    <Panel
      title="Completed Challenges"
      subtitle={!isLoading && challenges.length > 0
        ? challenges.length + (challenges.length === 1 ? ' challenge · ' : ' challenges · ') + totalPoints + ' points earned'
        : 'Submissions with video evidence'}
    >

      {isLoading && (
        <div className="usr-skel">
          <div className="skeleton usr-skel-block"></div>
          <div className="skeleton usr-skel-block"></div>
          <div className="skeleton usr-skel-block usr-skel-short"></div>
        </div>
      )}

      {!isLoading && failed && (
        <div className="usr-alert" role="alert">
          <b>The challenge history could not be loaded.</b>
          <span>The server did not answer. The rest of the profile is unaffected.</span>
          <Btn onClick={loadData}>Try again</Btn>
        </div>
      )}

      {!isLoading && !failed && challenges.length === 0 && (
        <div className="empty">This player has not completed a challenge yet.</div>
      )}

      {!isLoading && !failed && challenges.length > 0 && (
        <div className="scroll-body usr-ch-scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th className="usr-ch-act"><span className="usr-visually-hidden">Video</span></th>
                <th>Challenge</th>
                <th className="usr-ch-right">Points</th>
                <th className="usr-ch-right">Closes</th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        </div>
      )}

      {dialog}

    </Panel>
  )
}
